import { useEffect, useState } from "react";
import { AdminLayout } from "../../../components/admin/AdminLayout";
import { ForbiddenPanel } from "../../../components/admin/ForbiddenPanel";
import { authedFetch } from "../../../lib/api-client";
import { useT } from "../../../lib/use-t";

type VariantRow = {
  id: string;
  taskType: string;
  scenario: string;
  bulletsJson: string;
  status: string;
  createdAt: string;
};

export function AdminSchrijvenReviewQueuePage() {
  const t = useT();
  const [variants, setVariants] = useState<VariantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<Record<string, boolean>>({});

  function load() {
    setLoading(true);
    authedFetch("/admin/schrijven/review-queue")
      .then(async (r) => {
        if (r.status === 403) { setForbidden(true); return; }
        const body = await r.json();
        if (!body.ok) { setError(body.error?.message ?? "Fout"); return; }
        setVariants(body.data as VariantRow[]);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Fout"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function decide(id: string, decision: "approve" | "reject") {
    setDeciding((prev) => ({ ...prev, [id]: true }));
    try {
      await authedFetch(`/admin/schrijven/variants/${id}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision }),
      });
      setVariants((prev) => prev.filter((v) => v.id !== id));
    } finally {
      setDeciding((prev) => ({ ...prev, [id]: false }));
    }
  }

  if (forbidden) return <AdminLayout><ForbiddenPanel /></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
            {t("admin.schrijven.variantsEyebrow")}
          </p>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[var(--ink)] mt-1">
            {t("admin.schrijven.reviewQueueTitle")}
          </h1>
        </div>

        {error ? (
          <p role="alert" className="text-sm text-[var(--fail)]">{error}</p>
        ) : loading ? (
          <p className="text-sm text-[var(--ink-faint)]">{t("admin.schrijven.reviewQueueLoading")}</p>
        ) : variants.length === 0 ? (
          <div className="rounded-xl border border-[var(--border)] bg-[var(--panel)] p-6 text-center">
            <p className="text-sm text-[var(--ink-muted)]">{t("admin.schrijven.reviewQueueEmpty")}</p>
          </div>
        ) : (
          <ul className="space-y-4">
            {variants.map((v) => {
              const bullets = (() => {
                try { return JSON.parse(v.bulletsJson) as string[]; } catch { return []; }
              })();
              return (
                <li
                  key={v.id}
                  className="rounded-xl border border-[var(--border)] bg-[var(--panel)] p-5 space-y-4"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
                      {v.taskType}
                    </span>
                    <span className="ml-auto font-mono-badge text-xs text-[var(--ink-faint)]">
                      {v.id}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--ink)]">{v.scenario}</p>
                  {bullets.length > 0 ? (
                    <ul className="space-y-1">
                      {bullets.map((b, i) => (
                        <li key={i} className="text-xs text-[var(--ink-muted)] flex gap-2">
                          <span className="text-[var(--ink-faint)]">{i + 1}.</span>
                          {b}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => decide(v.id, "approve")}
                      disabled={deciding[v.id]}
                      className="inline-flex h-9 items-center rounded-[var(--radius)] bg-[var(--pass)] px-4 text-xs font-medium text-white disabled:opacity-50 hover:opacity-90 transition-opacity"
                    >
                      {t("admin.schrijven.approveButton")}
                    </button>
                    <button
                      type="button"
                      onClick={() => decide(v.id, "reject")}
                      disabled={deciding[v.id]}
                      className="inline-flex h-9 items-center rounded-[var(--radius)] border border-[var(--border)] bg-transparent px-4 text-xs text-[var(--ink-muted)] disabled:opacity-50 hover:bg-[var(--panel-soft)] transition-colors"
                    >
                      {t("admin.schrijven.rejectButton")}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
}
